/* eslint-disable @typescript-eslint/camelcase */
import { Typography } from "@material-ui/core"
import { Field, Formik, FormikHelpers, FormikProps } from "formik"
import { Form, SubmitButton, TextField } from "formik-material-ui-elements"
import React, { ReactNode, useEffect, useState } from "react"
import styled from "styled-components"
import * as yup from "yup"

const validationSchema = yup.object().shape({
  phone_number: yup.string().required("Required"),
})

export interface FormValues {
  phone_number: string
}

interface Props {
  onSubmit: (
    values: FormValues,
    actions: FormikHelpers<FormValues>,
  ) => void | Promise<any>
  initialValues?: FormValues
}

// Default form values
const defaultValues: FormValues = {
  phone_number: "",
}

// Styled version of your SubmitButton
const ForgotPasswordButton = styled(SubmitButton)`
  margin: auto;
  border-radius: 8px;
`

const ForgotPasswordForm: React.FC<Props> = ({
  onSubmit,
  initialValues = defaultValues,
}: Props) => {
  // State to keep track of the 30-second countdown
  const [timeLeft, setTimeLeft] = useState<number>(0)

  // Decrement timeLeft every second when > 0
  useEffect(() => {
    if (timeLeft <= 0) return

    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1)
    }, 1000)

    return () => clearInterval(timer)
  }, [timeLeft])

  // Wrap your onSubmit in a handler to start the countdown
  const handleSubmit = async (
    values: FormValues,
    actions: FormikHelpers<FormValues>,
  ) => {
    try {
      // Call the parent onSubmit
      await onSubmit(values, actions)
    } finally {
      // Once complete, start the 30-second cooldown
      setTimeLeft(30)
      actions.setSubmitting(false)
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validateOnBlur={false}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting }: FormikProps<FormValues>) => {
        // Disable if submitting or countdown is active
        const isDisabled = isSubmitting || timeLeft > 0

        return (
          <Form noValidate>
            <Field
              type="tel"
              autoComplete="tel"
              component={TextField}
              id="phone_number"
              label="Mobile Number"
              name="phone_number"
              color="secondary"
              size="small"
              required
            />

            <ForgotPasswordButton
              color="secondary"
              variant="contained"
              fullWidth
              loading={isSubmitting}
              disabled={isDisabled}
            >
              {/* Change button text if timer is active */}
              {timeLeft > 0 ? `Wait ${timeLeft}s` : "Send"}
            </ForgotPasswordButton>

            {/* Show countdown text under the button when timeLeft > 0 */}
            {timeLeft > 0 && (
              <Typography
                component="p" // or "div", "span", etc. if your TS demands it
                variant="body2"
                color="textSecondary"
                style={{ textAlign: "center", marginTop: "8px" }}
              >
                You can click the button again in {timeLeft} second
                {timeLeft === 1 ? "" : "s"}.
              </Typography>
            )}
          </Form>
        )
      }}
    </Formik>
  )
}

export default ForgotPasswordForm
